import type { ApiResponse } from '@/api/http';
import http, { getData } from '@/api/http';

/**
 * 系统时间服务：统一从后端获取服务器时间戳，并在前端维护偏移量。
 * 所有“当前时间”应通过此服务计算，避免用户设备时间不准导致逻辑错误。
 */
class TimeService {
  // 服务器时间与本地时间的差值（毫秒）：serverNow - localNow
  private offsetMs = 0;
  // 最近一次刷新时间戳（本地时间）
  private lastRefreshLocalMs = 0;
  // 刷新阈值（毫秒），超过该时间则建议重新向后端同步，默认 60 秒
  private readonly refreshIntervalMs = 60_000;

  /**
   * 主动向后端获取服务器当前时间戳（毫秒）并更新偏移量。
   */
  async refresh(): Promise<number> {
    const resp =
      await http.get<ApiResponse<number | { timestamp: number }>>('/api/system/timestamp');
    const raw = getData(resp) as number | { timestamp: number } | undefined;
    // 兼容后端仅返回 number 的情况
    const serverTs = typeof raw === 'number' ? raw : (raw?.timestamp ?? Date.now());
    const localTs = Date.now();
    this.offsetMs = serverTs - localTs;
    this.lastRefreshLocalMs = localTs;
    return serverTs;
  }

  /**
   * 获取“系统当前时间”（Date）。如超过刷新阈值会自动静默刷新（失败则使用旧偏移量）。
   */
  async nowAsync(): Promise<Date> {
    const localNow = Date.now();
    // 超过阈值则尝试刷新一次偏移量（不抛错，失败时继续使用旧偏移）
    if (localNow - this.lastRefreshLocalMs > this.refreshIntervalMs) {
      try {
        await this.refresh();
      } catch {
        // ignore
      }
    }
    return new Date(localNow + this.offsetMs);
  }

  /**
   * 获取“系统当前时间”（Date）。不会触发网络刷新，仅基于已知偏移量。
   */
  now(): Date {
    const localNow = Date.now();
    return new Date(localNow + this.offsetMs);
  }

  /**
   * 获取“系统当前时间戳”（毫秒）。不会触发网络刷新，仅基于已知偏移量。
   */
  nowTs(): number {
    return Date.now() + this.offsetMs;
  }

  /**
   * 初始化：在应用启动或用户登录后调用一次，确保初始偏移量。
   */
  async init(): Promise<void> {
    try {
      await this.refresh();
    } catch {
      // 后端不可用时保持 offset 为 0，后续使用本地时间
      this.offsetMs = 0;
      this.lastRefreshLocalMs = Date.now();
    }
  }
}

export const timeService = new TimeService();

/**
 * 便捷方法：获取系统当前时间（Date）。
 * 注意：这是异步方法，确保在路由守卫或应用初始化时调用 `timeService.init()`。
 */
export async function getSystemNow(): Promise<Date> {
  return timeService.now();
}

/**
 * 便捷方法：获取系统当前时间戳（毫秒）。
 */
export function getSystemTimestamp(): number {
  return timeService.nowTs();
}
